import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEventBySlug } from '../hooks/useEvents';
import { getImageUrl } from '../utils/helpers';
import { LoadingScreen } from '../components/ui/LoadingScreen';
import { ErrorState } from '../components/ui/ErrorState';

// ============================================================================
// ANIMATED COUNTER COMPONENT
// ============================================================================

const AnimatedCounter: React.FC<{ end: number; duration?: number; suffix?: string }> = ({
    end,
    duration = 2000,
    suffix = ''
}) => {
    const [count, setCount] = useState(0);
    const ref = useRef<HTMLSpanElement>(null);
    const hasAnimated = useRef(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !hasAnimated.current) {
                    hasAnimated.current = true;
                    let start = 0;
                    const increment = end / (duration / 16);
                    const timer = setInterval(() => {
                        start += increment;
                        if (start >= end) {
                            setCount(end);
                            clearInterval(timer);
                        } else {
                            setCount(Math.floor(start));
                        }
                    }, 16);
                }
            },
            { threshold: 0.5 }
        );

        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [end, duration]);

    return <span ref={ref}>{count}{suffix}</span>;
};

// ============================================================================
// DYNAMIC EVENT PAGE
// ============================================================================

const DynamicEventPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const { data: event, isLoading, error } = useEventBySlug(slug);
    const [scrollY, setScrollY] = useState(0);
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);

        const handleScroll = () => {
            setScrollY(window.scrollY);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    if (isLoading) {
        return <LoadingScreen />;
    }

    if (error || !event) {
        return (
            <ErrorState
                title="Evento não encontrado"
                description="O evento que você está procurando não existe ou pode ter sido removido."
            >
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 bg-brand-green text-white px-6 py-3 rounded-xl font-bold hover:bg-brand-darkGreen transition-all"
                >
                    <i className="fas fa-arrow-left"></i>
                    Voltar para a Home
                </Link>
            </ErrorState>
        );
    }

    const parallaxOffset = scrollY * 0.3;

    // Processamento de datas e horários
    const eventDate = new Date(event.date + 'T12:00:00');
    const displayDate = eventDate.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long' });
    const displayYear = eventDate.getFullYear().toString();
    
    // Parse de campos JSON ou defaults
    const highlights = event.highlights || [
        { icon: "fa-users", title: "Networking", desc: "Conecte-se com profissionais da região" },
        { icon: "fa-lightbulb", title: "Boas Práticas", desc: "Troca de experiências entre municípios" },
        { icon: "fa-shield-alt", title: "Fortalecimento", desc: "Rede de proteção mais unida" },
        { icon: "fa-coffee", title: "Café & Lanche", desc: "Momento de confraternização" },
    ];

    const agenda = event.agenda || [
        { time: "09:00", title: "Credenciamento", desc: "Recepção dos participantes" },
        { time: "09:30", title: "Abertura Oficial", desc: "Início das atividades" },
        { time: "12:00", title: "Encerramento", desc: "Considerações finais e agradecimentos" },
    ];

    const registrationLink = `/inscrever-se/${event.slug || event.id}`;

    // Funções de calendário
    const downloadICSFile = () => {
        const startDate = event.date.replace(/-/g, '');
        const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//ACONTURS Litoral//Eventos//PT
BEGIN:VEVENT
DTSTART:${startDate}T090000
DTEND:${startDate}T170000
SUMMARY:${event.title} ${displayYear}
LOCATION:${event.location}
DESCRIPTION:${event.description}
END:VEVENT
END:VCALENDAR`;

        const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Evento_Aconturs_${event.slug}.ics`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const getGoogleCalendarUrl = () => {
        const startDate = event.date.replace(/-/g, '');
        const params = new URLSearchParams({
            action: 'TEMPLATE',
            text: `${event.title} ${displayYear}`,
            dates: `${startDate}T090000/${startDate}T170000`,
            details: event.description || '',
            location: event.location || '',
        });
        return `https://calendar.google.com/calendar/render?${params.toString()}`;
    };

    return (
        <div className="min-h-screen bg-slate-50 font-sans text-slate-800 antialiased overflow-x-hidden">
            {/* FLOATING NAV */}
            <nav
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrollY > 100
                    ? 'bg-white/90 backdrop-blur-xl shadow-lg shadow-slate-200/50 py-3'
                    : 'bg-transparent py-6'
                    }`}
            >
                <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
                    <button
                        onClick={() => navigate(-1)}
                        className={`group flex items-center gap-2 transition-all ${scrollY > 100 ? 'text-slate-600 hover:text-brand-darkGreen' : 'text-white hover:text-white/80'
                            }`}
                    >
                        <span className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${scrollY > 100
                            ? 'bg-slate-100 group-hover:bg-brand-green/10'
                            : 'bg-white/10 border border-white/20 group-hover:bg-white/20'
                            }`}>
                            <i className="fas fa-arrow-left text-sm"></i>
                        </span>
                        <span className="hidden sm:inline text-sm font-medium">Voltar</span>
                    </button>

                    <div className="flex items-center gap-3">
                        <img
                            src="/assets/LOGO_AZUL_ACONTURS.png"
                            alt="ACONTURS"
                            className={`h-10 md:h-12 object-contain transition-opacity ${scrollY > 100 ? 'opacity-100' : 'opacity-90'}`}
                        />
                    </div>

                    <Link
                        to={registrationLink}
                        className={`px-5 py-2.5 rounded-full font-bold text-sm transition-all ${scrollY > 100
                            ? 'bg-brand-green text-white shadow-lg shadow-brand-green/20 hover:bg-brand-darkGreen'
                            : 'bg-white text-brand-darkGreen hover:bg-white/90 shadow-lg'
                            }`}
                    >
                        <span className="hidden sm:inline">Inscreva-se</span>
                        <i className="fas fa-arrow-right sm:hidden"></i>
                    </Link>
                </div>
            </nav>

            {/* HERO */}
            <section className="relative min-h-screen flex flex-col overflow-hidden">
                <div
                    className="absolute inset-0 transition-transform duration-100"
                    style={{ transform: `translateY(${parallaxOffset}px)` }}
                >
                    <div className="absolute inset-0 bg-gradient-to-br from-brand-green via-brand-darkGreen to-brand-darkGreen"></div>
                    <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-brand-yellow/20 rounded-full blur-[150px] -translate-y-1/2 translate-x-1/4"></div>
                    <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-white/10 rounded-full blur-[120px] translate-y-1/2 -translate-x-1/4"></div>
                    <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`, backgroundSize: '40px 40px' }}></div>
                </div>

                <div className="relative z-10 container mx-auto px-6 pt-28 pb-32 flex-1 flex flex-col items-center justify-center text-white">
                    <div className="max-w-4xl mx-auto text-center">
                        <div className="relative group mb-8">
                            <div className="absolute -inset-4 bg-gradient-to-r from-brand-yellow/30 via-white/10 to-brand-yellow/30 rounded-[2rem] blur-2xl opacity-60 group-hover:opacity-80 transition-opacity duration-500"></div>
                            <img
                                src={getImageUrl(event.image) || '/assets/convite_cafe.jpeg'}
                                alt={event.title}
                                className="relative w-full max-w-3xl mx-auto rounded-3xl shadow-2xl border-4 border-white/20 group-hover:scale-[1.02] transition-transform duration-500"
                            />
                        </div>

                        <div className="flex flex-wrap justify-center gap-4 mb-10">
                            <div className="group flex items-center gap-3 px-6 py-3 rounded-2xl bg-white/10 border border-white/20 backdrop-blur-sm hover:bg-white/20 transition-all cursor-default">
                                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                                    <i className="fas fa-calendar-alt text-brand-yellow"></i>
                                </div>
                                <div className="text-left">
                                    <span className="block text-xs text-white/60 uppercase tracking-wider">Data</span>
                                    <span className="block text-white font-semibold">{displayDate}</span>
                                </div>
                            </div>

                            <div className="group flex items-center gap-3 px-6 py-3 rounded-2xl bg-white/10 border border-white/20 backdrop-blur-sm hover:bg-white/20 transition-all cursor-default">
                                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                                    <i className={`fas ${Number(event.is_online_only) === 1 ? 'fa-video text-brand-green' : 'fa-map-marker-alt text-brand-red'}`}></i>
                                </div>
                                <div className="text-left">
                                    <span className="block text-xs text-white/60 uppercase tracking-wider">Local</span>
                                    <span className="block text-white font-semibold">{Number(event.is_online_only) === 1 ? 'Evento Online' : event.location}</span>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            {event.status === 'past' ? (
                                event.photos_url ? (
                                    <a 
                                        href={event.photos_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="group relative inline-flex items-center justify-center gap-3 bg-brand-yellow text-brand-darkGreen px-10 py-5 rounded-full font-extrabold text-lg shadow-[0_8px_30px_rgba(254,193,14,0.4)] hover:shadow-[0_12px_40px_rgba(254,193,14,0.5)] hover:-translate-y-1 transition-all duration-300 overflow-hidden"
                                    >
                                        <span className="relative z-10 flex items-center gap-3">
                                            <i className="fas fa-camera"></i>
                                            VER GALERIA DE FOTOS
                                        </span>
                                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                                    </a>
                                ) : (
                                    <div className="bg-white/10 border border-white/20 backdrop-blur-md px-10 py-5 rounded-full text-white font-bold flex items-center gap-3 cursor-default">
                                        <i className="fas fa-history text-brand-yellow"></i>
                                        FOTOS AINDA NÃO ESTÃO PRONTAS
                                    </div>
                                )
                            ) : (
                                <Link
                                    to={registrationLink}
                                    className="group relative inline-flex items-center justify-center gap-3 bg-brand-yellow text-brand-darkGreen px-10 py-5 rounded-full font-extrabold text-lg shadow-[0_8px_30px_rgba(254,193,14,0.4)] hover:shadow-[0_12px_40px_rgba(254,193,14,0.5)] hover:-translate-y-1 transition-all duration-300 overflow-hidden"
                                >
                                    <span className="relative z-10 flex items-center gap-3">
                                        <i className="fas fa-ticket-alt"></i>
                                        Garantir Minha Vaga
                                    </span>
                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                                </Link>
                            )}

                            {event.status !== 'past' && (
                                <button
                                    onClick={() => setIsCalendarOpen(!isCalendarOpen)}
                                    className="inline-flex items-center justify-center gap-3 bg-white/10 border border-white/30 text-white px-8 py-5 rounded-full font-bold text-lg hover:bg-white/20 transition-all backdrop-blur-sm"
                                >
                                    <i className="fas fa-calendar-plus"></i>
                                    Lembrar-me
                                </button>
                            )}
                        </div>

                        {isCalendarOpen && (
                            <div className="mt-4 inline-flex flex-col sm:flex-row gap-3 p-4 rounded-2xl bg-white/10 border border-white/20 backdrop-blur-xl animate-in slide-in-from-top-4 duration-300">
                                <button onClick={downloadICSFile} className="flex items-center gap-2 px-5 py-3 rounded-xl bg-white/20 hover:bg-white/30 text-white text-sm font-medium transition-all">
                                    <i className="fas fa-download"></i> Apple / Outlook
                                </button>
                                <a href={getGoogleCalendarUrl()} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-5 py-3 rounded-xl bg-white/20 hover:bg-white/30 text-white text-sm font-medium transition-all">
                                    <i className="fab fa-google"></i> Google Calendar
                                </a>
                            </div>
                        )}
                    </div>
                </div>

                <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none z-20">
                    <svg className="relative block w-full h-[80px] md:h-[120px]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
                        <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" fill="#F8FAFC"></path>
                    </svg>
                </div>
            </section>

            {/* BENTO GRID - HIGHLIGHTS */}
            <section className="relative py-24 bg-slate-50">
                <div className="container mx-auto px-6">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-16">
                            <span className="inline-block px-4 py-1.5 rounded-full bg-brand-green/10 border border-brand-green/20 text-brand-green text-xs font-bold uppercase tracking-widest mb-4">
                                O que esperar
                            </span>
                            <h2 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-4">
                                Um encontro <span className="text-brand-green">transformador</span>
                            </h2>
                            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                                {event.description}
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                            {highlights.map((item: any, index: number) => (
                                <div
                                    key={index}
                                    className="group relative p-6 rounded-3xl bg-white border border-slate-100 shadow-lg shadow-slate-200/50 hover:shadow-xl hover:shadow-brand-green/10 hover:-translate-y-2 transition-all duration-500 overflow-hidden"
                                >
                                    <div className="absolute inset-0 bg-gradient-to-br from-brand-green/5 to-brand-yellow/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                    <div className="relative z-10">
                                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-green to-brand-darkGreen flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-500 shadow-lg shadow-brand-green/20">
                                            <i className={`fas ${item.icon} text-xl text-white`}></i>
                                        </div>
                                        <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                                        <p className="text-sm text-slate-500">{item.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* AGENDA */}
            <section className="relative py-24 bg-white border-y border-slate-100">
                <div className="container mx-auto px-6">
                    <div className="max-w-4xl mx-auto">
                        <span className="inline-block px-4 py-1.5 rounded-full bg-brand-yellow/10 border border-brand-yellow/30 text-brand-darkGreen text-xs font-bold uppercase tracking-widest mb-4">
                            <i className="fas fa-clock mr-2"></i>Programação
                        </span>
                        <h2 className="text-4xl font-extrabold text-slate-900 mb-8">
                            Agenda do dia
                        </h2>

                        <div className="space-y-4">
                            {agenda.map((item: any, index: number) => (
                                <div
                                    key={index}
                                    className="group flex items-start gap-4 p-5 rounded-2xl bg-slate-50 border border-slate-100 hover:bg-white hover:border-brand-green/20 hover:shadow-lg hover:shadow-brand-green/5 transition-all duration-300"
                                >
                                    <div className="shrink-0 w-16 h-16 rounded-xl bg-brand-green flex flex-col items-center justify-center group-hover:bg-brand-darkGreen transition-colors shadow-lg shadow-brand-green/20">
                                        <span className="text-lg font-bold text-white">{item.time.split(':')[0]}h</span>
                                        <span className="text-xs text-white/70">{item.time.split(':')[1] || '00'}</span>
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="text-slate-900 font-bold mb-1">{item.title}</h4>
                                        <p className="text-slate-500 text-sm">{item.desc}</p>
                                    </div>
                                    <i className="fas fa-chevron-right text-slate-300 group-hover:text-brand-green group-hover:translate-x-1 transition-all self-center"></i>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* MAP - Only show if NOT online only */}
            {Number(event.is_online_only) !== 1 && (
                <section className="relative py-24 bg-slate-50">
                <div className="container mx-auto px-6">
                    <div className="max-w-6xl mx-auto">
                        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                            <div className="lg:col-span-2 flex flex-col justify-center">
                                <span className="inline-block w-fit px-4 py-1.5 rounded-full bg-brand-red/10 border border-brand-red/20 text-brand-red text-xs font-bold uppercase tracking-widest mb-4">
                                    <i className="fas fa-map-marker-alt mr-2"></i>Localização
                                </span>
                                <h2 className="text-4xl font-extrabold text-slate-900 mb-4">
                                    {event.location}
                                </h2>
                                
                                <div className="space-y-4 mt-6">
                                    <div className="flex items-center gap-4 p-4 rounded-xl bg-white border border-slate-100 shadow-sm">
                                        <div className="w-10 h-10 rounded-lg bg-brand-yellow/10 flex items-center justify-center">
                                            <i className="fas fa-car text-brand-yellow text-lg"></i>
                                        </div>
                                        <span className="text-slate-700 font-medium">Estacionamento no local</span>
                                    </div>
                                    <div className="flex items-center gap-4 p-4 rounded-xl bg-white border border-slate-100 shadow-sm">
                                        <div className="w-10 h-10 rounded-lg bg-brand-green/10 flex items-center justify-center">
                                            <i className="fas fa-wheelchair text-brand-green text-lg"></i>
                                        </div>
                                        <span className="text-slate-700 font-medium">Acessibilidade completa</span>
                                    </div>
                                </div>

                                <a
                                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="mt-6 inline-flex items-center gap-2 text-brand-green font-bold hover:text-brand-darkGreen transition-colors"
                                >
                                    <i className="fas fa-directions"></i>
                                    Abrir no Google Maps
                                    <i className="fas fa-external-link-alt text-xs"></i>
                                </a>
                            </div>

                            <div className="lg:col-span-3 h-[400px] rounded-3xl overflow-hidden border border-slate-200 shadow-xl">
                                <iframe
                                    src={`https://maps.google.com/maps?q=${encodeURIComponent(event.location)}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                                    width="100%"
                                    height="100%"
                                    style={{ border: 0 }}
                                    allowFullScreen
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                    title="Local do evento"
                                ></iframe>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            )}

            {/* FINAL CTA */}
            <section className="relative py-32 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-brand-green via-brand-darkGreen to-brand-darkGreen"></div>
                <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-brand-yellow/30 rounded-full blur-[150px]"></div>

                <div className="relative z-10 container mx-auto px-6 text-center text-white">
                    <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-8 backdrop-blur-sm border border-white/20">
                        <i className="fas fa-ticket-alt text-3xl text-brand-yellow"></i>
                    </div>

                    <h2 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight">
                        Garanta sua vaga!
                    </h2>
                    <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
                        Participe deste encontro e ajude a fortalecer a rede de proteção à infância e adolescência em nossa região.
                    </p>

                    <Link
                        to={registrationLink}
                        className="group relative inline-flex items-center justify-center gap-3 bg-white text-brand-darkGreen px-12 py-6 rounded-full font-extrabold text-xl shadow-2xl hover:-translate-y-2 transition-all duration-300 overflow-hidden"
                    >
                        <span className="relative z-10 flex items-center gap-3">
                            <i className="fas fa-paper-plane"></i>
                            Inscreva-se Agora
                        </span>
                        <div className="absolute inset-0 bg-brand-yellow scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300"></div>
                    </Link>
                </div>
            </section>

            {/* FOOTER */}
            <footer className="bg-slate-900 text-white py-12">
                <div className="container mx-auto px-6 text-center">
                    <div className="mb-8">
                        <span className="text-2xl font-bold tracking-widest uppercase">ACONTURS <span className="text-brand-green">Litoral</span></span>
                    </div>
                    <div className="flex justify-center gap-6 mb-8">
                        <a href="#" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-brand-green transition-all"><i className="fab fa-instagram"></i></a>
                        <a href="#" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-brand-green transition-all"><i className="fab fa-facebook-f"></i></a>
                        <a href="#" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-brand-green transition-all"><i className="fab fa-whatsapp"></i></a>
                    </div>
                    <p className="text-white/40 text-sm">© {displayYear} ACONTURS Litoral • Todos os direitos reservados</p>
                </div>
            </footer>
        </div>
    );
};

export default DynamicEventPage;
